import React, { createContext, useState, useContext, useEffect } from 'react';
import { LoginResponse, UserData } from '../types/auth';
import { login as apiLogin, register as apiRegister, getCurrentUser } from '../services/authService';
import api from '../services/apiConfig';

interface AuthContextType {
  user: UserData | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  register: (username: string, email: string, password: string) => Promise<any>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  login: async () => ({ id: 0, username: '', email: '' }),
  register: async () => ({}),
  logout: () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    // Check if user is logged in on initial load
    const checkAuthStatus = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        // Not authenticated, that's okay
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await apiLogin(email, password);
    setUser(response);
    setIsAuthenticated(true);
    return response;
  };

  const register = async (username: string, email: string, password: string) => {
    const response = await apiRegister(username, email, password);
    return response;
  };

  const logout = () => {
    // Clear user data and authentication state
    setUser(null);
    setIsAuthenticated(false);
    
    // Call logout API endpoint
    api.post('/api/auth/logout')
      .catch(error => {
        console.error('Logout error:', error);
      });
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};